import { useEffect, useState } from 'react';
import { ListTodo, Plus, Check, X, Flag, Repeat, UserRound, CircleSlash, History, Camera, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevRight } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { listWeekTasks, createTask, updateTask, deleteTask, listAssignees, Task, WeekTasksResp, Assignee } from '../tasks/api';
import TemplatesInline from '../tasks/TemplatesInline';
import TaskDetailInline from '../tasks/TaskDetailInline';
import HistoryInline from '../tasks/HistoryInline';
import { Skeleton } from '../../components/Skeleton';
import { readCache, writeCache } from '../../services/localCache';

type TareaSemana = WeekTasksResp['data'][number];

/** Lunes de la semana de una fecha (o de hoy), en local. */
function lunesDe(iso?: string) {
  const base = iso ? new Date(iso + 'T12:00:00') : new Date();
  const dow = (base.getDay() + 6) % 7;
  const d = new Date(base); d.setDate(base.getDate() - dow);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Corre N semanas desde una fecha ISO. */
function corre(iso: string, semanas: number) {
  const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + semanas * 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** «27 jul – 2 ago» (y el año solo si no es el actual). */
function rangoBonito(from: string, to: string) {
  const a = new Date(from + 'T12:00:00'), b = new Date(to + 'T12:00:00');
  const f = (d: Date) => d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  const anio = a.getFullYear() !== new Date().getFullYear() ? ` ${a.getFullYear()}` : '';
  return `${f(a)} – ${f(b)}${anio}`;
}

/** Etiqueta y color del chip de vencimiento. */
function dueChip(t: Task): { label: string; cls: string } | null {
  if (t.days_until == null) return null;
  if (t.days_until < 0) return { label: t.days_until === -1 ? 'venció ayer' : `venció hace ${-t.days_until}d`, cls: 'bg-rose-500/15 text-rose-600' };
  if (t.days_until === 0) return { label: 'vence hoy', cls: 'bg-amber-500/15 text-amber-600' };
  if (t.days_until === 1) return { label: 'mañana', cls: 'bg-brand-blue/10 text-brand-blue' };
  return { label: `en ${t.days_until}d`, cls: 'bg-surface-2 text-content-3' };
}

/** Primer nombre para el chip de responsable. */
const firstName = (full: string) => full.trim().split(/\s+/)[0] || full;

type CachedTasks = { tasks: TareaSemana[]; summary: WeekTasksResp['summary'] | null };

export default function TasksPanel({ className = '' }: { className?: string }) {
  const toast = useToast();
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const cached = readCache<CachedTasks>('tasks-week');
  const [tasks, setTasks] = useState<TareaSemana[] | null>((cached?.tasks as TareaSemana[]) ?? null);
  const [summary, setSummary] = useState<WeekTasksResp['summary'] | null>((cached?.summary as WeekTasksResp['summary']) ?? null);
  // TABLERO SEMANAL (Karla 29-jul): la semana que se está viendo (lunes) y el filtro «solo mías».
  const [semana, setSemana] = useState<string>(() => lunesDe());
  const [rango, setRango] = useState<{ from: string; to: string } | null>(null);
  const [mias, setMias] = useState(false);
  // Filtro «ver las de Fulano» (F2 plan de tareas, 17-ago): client-side sobre la semana ya
  // cargada — '' = todas, 'none' = sin responsable, o el id del assignee. Excluyente con «mías».
  const [quien, setQuien] = useState('');
  const [verLuego, setVerLuego] = useState(false);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [due, setDue] = useState('');
  const [high, setHigh] = useState(false);
  const [who, setWho] = useState('');
  const [saving, setSaving] = useState(false);
  // Vista del bloque: tareas | historial | recurrentes — todo en línea, sin modales.
  const [view, setView] = useState<'tareas' | 'historial' | 'recurrentes'>('tareas');
  // Tarea expandida en línea (acordeón); antes era un modal.
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = () => listWeekTasks(semana, mias).then((r) => {
    setTasks(r.data); setSummary(r.summary); setRango(r.week);
    // Solo se cachea la vista por defecto (semana actual, todas): es la que se pinta al entrar.
    if (semana === lunesDe() && !mias) writeCache('tasks-week', { tasks: r.data, summary: r.summary });
  }).catch(() => setTasks((prev) => prev ?? []));
  useEffect(() => { load(); }, [semana, mias]);
  useEffect(() => { listAssignees().then((r) => setAssignees(r.data)).catch(() => {}); }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      await createTask({ title: t, description: desc.trim() || null, complete_by: due || null, priority: high ? 'high' : 'normal', assigned_user_id: who ? Number(who) : null });
      setTitle(''); setDesc(''); setDue(''); setHigh(false); setWho('');
      await load();
    } finally { setSaving(false); }
  };

  const toggle = async (t: Task) => {
    const next = t.status === 'open' ? 'done' : 'open';
    // Candado de foto (F4): el backend rechazaría con 400; aquí se explica y se abre el detalle
    if (next === 'done' && t.requires_photo === 1 && !t.photo_path) {
      toast.error('Esta tarea requiere foto de evidencia — ábrela y tómala');
      setExpandedId(t.task_id);
      return;
    }
    setTasks((prev) => prev?.map((x) => x.task_id === t.task_id ? { ...x, status: next } : x) || null);
    await updateTask(t.task_id, { status: next });
    load();
  };

  // Omitir con motivo (obligatorio) — para recurrentes que hoy no se pudieron hacer.
  const skip = async (t: Task) => {
    const reason = window.prompt(`Omitir "${t.title}" — ¿por qué no se hizo?`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error('El motivo es obligatorio para omitir'); return; }
    await updateTask(t.task_id, { status: 'skipped', skip_reason: reason.trim() });
    load();
  };

  const remove = async (t: Task) => {
    setTasks((prev) => prev?.filter((x) => x.task_id !== t.task_id) || null);
    try { await deleteTask(t.task_id); } catch (e: any) { toast.error(e?.message || 'No se pudo eliminar'); }
    load();
  };

  /** Un renglón de tarea (se reusa en los 4 grupos del tablero semanal). */
  const fila = (t: TareaSemana) => {
    const resolved = t.status !== 'open';
    const chip = resolved ? null : dueChip(t);
    const expanded = expandedId === t.task_id;
    return (
      <li key={t.task_id} className="rounded-lg px-1 py-1.5 transition hover:bg-surface-2">
        <div className="group flex items-center gap-2.5">
        <button onClick={() => toggle(t)} title={resolved ? 'Reabrir' : 'Completar'}
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
            t.status === 'done' ? 'border-emerald-500 bg-emerald-500 text-white'
            : t.status === 'skipped' ? 'border-amber-500 bg-amber-500 text-white'
            : 'border-content-3 hover:border-accent'}`}>
          {t.status === 'done' && <Check size={13} />}
          {t.status === 'skipped' && <CircleSlash size={12} />}
        </button>
        {/* Tocar el título expande/colapsa el detalle en línea (antes modal) */}
        <button type="button" onClick={() => setExpandedId(expanded ? null : t.task_id)}
          className={`min-w-0 flex-1 truncate text-left text-sm ${resolved ? 'text-content-3 line-through' : 'text-content'}`}
          title="Ver / editar">
          {t.template_id != null && <Repeat size={11} className="mr-1 inline text-content-3" />}
          {t.priority === 'high' && !resolved && <Flag size={11} className="mr-1 inline text-rose-500" />}
          {t.requires_photo === 1 && !resolved && <Camera size={11} className={`mr-1 inline ${t.photo_path ? 'text-emerald-500' : 'text-amber-500'}`} />}
          {t.title}
        </button>
        {t.assignee && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold text-accent-ink" title={t.assignee}>
            <UserRound size={10} /> {firstName(t.assignee)}
          </span>
        )}
        {chip && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip.cls}`}>{chip.label}</span>}
        {!resolved && t.template_id != null && (
          <button onClick={() => skip(t)} title="Omitir con motivo"
            className="hover-reveal shrink-0 text-content-3 opacity-0 transition hover:text-amber-500 group-hover:opacity-100">
            <CircleSlash size={14} />
          </button>
        )}
        {t.template_id == null && (
          <button onClick={() => remove(t)} title="Eliminar"
            className="hover-reveal shrink-0 text-content-3 opacity-0 transition hover:text-rose-500 group-hover:opacity-100">
            <X size={14} />
          </button>
        )}
        </div>
        {expanded && (
          <TaskDetailInline key={t.task_id} task={t} assignees={assignees}
            onClose={(changed) => { setExpandedId(null); if (changed) load(); }}
            onPhotoChanged={load} />
        )}
      </li>
    );
  };

  return (
    <div className={`flex flex-col rounded-2xl border border-line bg-surface p-5 shadow-sm ${className}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
          <ListTodo size={16} className="text-accent-ink" /> Tareas
        </h3>
        <div className="flex items-center gap-2">
          {summary && (summary.overdue > 0 || summary.today > 0 || summary.carried > 0) && (
            <div className="flex items-center gap-1.5 text-[10px] font-semibold">
              {summary.carried > 0 && <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-violet-600 dark:text-violet-400" title="Vienen de semanas pasadas sin cumplirse">{summary.carried} {summary.carried === 1 ? 'arrastrada' : 'arrastradas'}</span>}
              {summary.overdue > 0 && <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-rose-600">{summary.overdue} {summary.overdue === 1 ? 'vencida' : 'vencidas'}</span>}
              {summary.today > 0 && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-600">{summary.today} hoy</span>}
            </div>
          )}
          {/* Alternan la vista del bloque (sin modales); volver a tareas refresca la lista */}
          <button onClick={() => { if (view === 'historial') { setView('tareas'); load(); } else setView('historial'); }}
            aria-pressed={view === 'historial'} title="Historial: qué se hizo y quién lo marcó"
            className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium transition ${view === 'historial' ? 'border-accent/50 bg-accent/15 text-accent-ink' : 'border-line text-content-2 hover:bg-surface-2 hover:text-content'}`}>
            <History size={13} /> {view === 'historial' ? 'Volver a tareas' : 'Historial'}
          </button>
          <button onClick={() => { if (view === 'recurrentes') { setView('tareas'); load(); } else setView('recurrentes'); }}
            aria-pressed={view === 'recurrentes'} title="Tareas recurrentes"
            className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium transition ${view === 'recurrentes' ? 'border-accent/50 bg-accent/15 text-accent-ink' : 'border-line text-content-2 hover:bg-surface-2 hover:text-content'}`}>
            <Repeat size={13} /> {view === 'recurrentes' ? 'Volver a tareas' : 'Recurrentes'}
          </button>
        </div>
      </div>

      {view === 'historial' ? <HistoryInline />
        : view === 'recurrentes' ? <TemplatesInline assignees={assignees} />
        : <>

      {/* TABLERO POR SEMANA (Karla 29-jul): «cada semana se vacía tu tablero y solo quedan las
          que no se hayan cumplido». Se navega entre semanas y se puede ver solo lo mío. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface-2/50 px-2 py-1.5">
        <button onClick={() => setSemana((s) => corre(s, -1))} title="Semana anterior"
          className="rounded p-1 text-content-3 transition hover:bg-surface-2 hover:text-content"><ChevronLeft size={15} /></button>
        <span className="text-xs font-semibold text-content">
          {rango ? rangoBonito(rango.from, rango.to) : 'Semana'}
          {semana === lunesDe() && <span className="ml-1.5 rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] text-accent-ink">esta semana</span>}
        </span>
        <button onClick={() => setSemana((s) => corre(s, 1))} title="Semana siguiente"
          className="rounded p-1 text-content-3 transition hover:bg-surface-2 hover:text-content"><ChevronRight size={15} /></button>
        {semana !== lunesDe() && (
          <button onClick={() => setSemana(lunesDe())}
            className="rounded-lg border border-line px-2 py-0.5 text-[11px] font-medium text-content-2 transition hover:bg-surface-2">Esta semana</button>
        )}
        <select value={quien} onChange={(e) => { setQuien(e.target.value); if (e.target.value) setMias(false); }}
          title="Ver solo las tareas de una persona"
          className="ml-auto max-w-[130px] rounded-lg border border-line bg-surface-2 px-1.5 py-0.5 text-[11px] text-content-2 outline-none focus:border-accent/50">
          <option value="">Todas las personas</option>
          <option value="none">Sin responsable</option>
          {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <button onClick={() => { setMias((v) => !v); setQuien(''); }} aria-pressed={mias} title="Solo las tareas asignadas a mí"
          className={`flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-medium transition ${mias ? 'border-accent/50 bg-accent/15 text-accent-ink' : 'border-line text-content-2 hover:bg-surface-2'}`}>
          <UserRound size={12} /> Solo mías
        </button>
      </div>

      {/* Alta rápida: título en su renglón; debajo responsable, vencimiento con
          etiqueta y prioridad como chip con TEXTO (en móvil los íconos solos no se entienden). */}
      <form onSubmit={add} className="mb-3 space-y-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nueva tarea…"
          className="w-full rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-sm text-content outline-none focus:border-accent/50" />
        {/* Detalles: aparece al empezar a escribir (pedido de Karla 29-jul — el campo ya existía
            en la API y en el detalle, pero no en el alta, así que se perdía la explicación).
            Solo con título escrito para no gastar renglón cuando el bloque está en reposo. */}
        {title.trim() && (
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2}
            placeholder="Detalles (opcional): cómo se hace, dónde, con quién…"
            className="w-full resize-y rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-sm text-content outline-none focus:border-accent/50" />
        )}
        <div className="flex flex-wrap items-center gap-2">
          <select value={who} onChange={(e) => setWho(e.target.value)} title="Responsable"
            className="max-w-[140px] rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-xs text-content-2 outline-none focus:border-accent/50">
            <option value="">Sin responsable</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <label className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-xs text-content-3">
            Vence
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
              className="bg-transparent text-xs text-content-2 outline-none" />
          </label>
          <button type="button" onClick={() => setHigh((v) => !v)} title="Marcar como prioridad alta"
            aria-pressed={high}
            className={`flex h-8 items-center gap-1 rounded-lg border px-2 text-xs font-medium transition ${high ? 'border-rose-400 bg-rose-500/15 text-rose-600' : 'border-line text-content-3 hover:text-content'}`}>
            <Flag size={13} /> Alta
          </button>
          <button type="submit" disabled={!title.trim() || saving}
            className="ml-auto flex h-8 items-center gap-1 rounded-lg bg-accent px-3 text-xs font-semibold text-on-accent transition hover:opacity-90 disabled:opacity-40">
            <Plus size={14} /> Agregar
          </button>
        </div>
      </form>

      {tasks === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
      ) : tasks.length === 0 ? (
        <p className="py-6 text-center text-sm text-content-3">
          {semana === lunesDe() ? 'Sin tareas pendientes. 🎉' : 'Nada en esa semana.'}
        </p>
      ) : (
        // flex-1: la lista crece para llenar la tarjeta cuando va junto a Clases de hoy;
        // el max-h la acota para que no sea ella la que estire la fila.
        <ul className="-mr-1 min-h-0 max-h-80 flex-1 space-y-1 overflow-y-auto pr-1 [scrollbar-width:thin]">
          {(() => {
            const delQuien = (x: TareaSemana) => !quien
              || (quien === 'none' ? x.assigned_user_id == null : String(x.assigned_user_id) === quien);
            const g = (b: string) => tasks!.filter((x) => x.bucket === b && delQuien(x));
            const arrastradas = g('carried'), estaSemana = g('week'), hechas = g('done'), luego = g('later');
            if (!arrastradas.length && !estaSemana.length && !hechas.length && !luego.length) {
              return <li className="py-4 text-center text-sm text-content-3">Nada de esa persona esta semana.</li>;
            }
            const titulo = (txt: string, cls: string) => (
              <li key={`h-${txt}`} className={`px-1 pt-2 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>{txt}</li>
            );
            return (
              <>
                {/* Lo que NO se cumplió y se arrastra: es la parte que ella pidió que se quede */}
                {arrastradas.length > 0 && titulo(arrastradas.length === 1 ? 'No se cumplió y sigue aquí (1)' : `No se cumplieron y siguen aquí (${arrastradas.length})`, 'text-violet-600 dark:text-violet-400')}
                {arrastradas.map(fila)}
                {estaSemana.length > 0 && (arrastradas.length > 0 || hechas.length > 0) && titulo('De esta semana', 'text-content-3')}
                {estaSemana.map(fila)}
                {hechas.length > 0 && titulo(hechas.length === 1 ? 'Hecha esta semana (1)' : `Hechas esta semana (${hechas.length})`, 'text-emerald-600 dark:text-emerald-400')}
                {hechas.map(fila)}
                {/* Las de fecha futura no ensucian la semana, pero no se esconden: van colapsadas */}
                {luego.length > 0 && (
                  <li key="h-luego" className="px-1 pt-2">
                    <button onClick={() => setVerLuego((v) => !v)}
                      className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-content-3 transition hover:text-content-2">
                      {verLuego ? <ChevronDown size={12} /> : <ChevRight size={12} />} Más adelante ({luego.length})
                    </button>
                  </li>
                )}
                {verLuego && luego.map(fila)}
              </>
            );
          })()}
        </ul>
      )}

      </>}
    </div>
  );
}
